import React, { useEffect, useState } from "react";
import { connect } from "react-redux";
import { useBoolean } from "react-hanger";
import { Helmet } from "react-helmet-async";
import styled from "styled-components";
import { useDropzone } from "react-dropzone";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Container from "@mui/material/Container";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogActions from "@mui/material/DialogActions";
import Typography from "@mui/material/Typography";
import {
  DataGrid,
  GridColDef,
} from "@mui/x-data-grid";

import { siteConstants } from "../../../constants";
import { alertActions, csActions } from "../../../redux/actions";
import GridCellExpand from "../../GridCellExpand";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";

const getDropzoneBackgroundColor = (props) => {
  if (props.isDragActive)
    return "#fafafa";

  return "#ebebeb";
}

const DropzoneContainer = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-top: 20px;
  padding: 20px;
  border-width: 1px;
  border-radius: 4px;
  border-style: dashed;
  border-color: #aaaaaa;
  background-color: ${props => getDropzoneBackgroundColor(props)};
  color: #8a8a8a;
  outline: none;
`;

const StyledDropzone = (props) => {
  const {
    acceptedFiles,
    fileRejections,
    getRootProps,
    getInputProps,
    isDragActive,
    isDragAccept,
    isDragReject,
  } = useDropzone({
    accept: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    maxFiles: 1,
    multiple: false,
  });

  useEffect(() => {
    if (acceptedFiles.length > 0)
      props.onFileAccepted(acceptedFiles[0]);
  }, [acceptedFiles]);

  useEffect(() => {
    if (fileRejections.length > 0)
      props.onFileRejected();
  }, [fileRejections]);

  return (
    <>
      <DropzoneContainer {...getRootProps({ isDragActive, isDragAccept, isDragReject })}>
        <input {...getInputProps()} />
        <Typography align="center" variant="subtitle2">
          <p>Drag 'n' drop guidance database file here, or click to select file</p>
        </Typography>
        <CloudUploadIcon />
      </DropzoneContainer>
      {acceptedFiles.length > 0 &&
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center" }}>
          <Typography variant="caption">
            {acceptedFiles[0].name} ({(acceptedFiles[0].size / 1000).toFixed(1)} KB)
          </Typography>
        </Box>
      }
      {fileRejections.length > 0 &&
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center" }}>
          <Typography variant="caption">
            {fileRejections[0].file.name} ({(fileRejections[0].file.size / 1000).toFixed(1)} KB)
          </Typography>
        </Box>
      }
    </>
  )
}

const GuidanceDatabasePage = (props) => {
  const [guidance, setGuidance] = useState([]);
  const [guidanceFile, setGuidanceFile] = useState(null);
  const isConfirmationDialogOpen = useBoolean(false);

  const renderCellExpand = (params) => {
    return (
      <GridCellExpand
        value={params.value || ""}
        width={"40%"}
      />
    );
  }

  const columns: GridColDef[] = [
    { field: "skill", headerName: "Skill", width: 120, },
    { field: "theme", headerName: "Theme", width: 120, },
    {
      field: "csText",
      headerName: "CS Text",
      flex: 1,
      renderCell: renderCellExpand,
    },
    {
      field: "guidance",
      headerName: "Guidance",
      flex: 1,
      renderCell: renderCellExpand,
    },
  ];

  const onFileAccepted = (file) => {
    props.alertReset();
    setGuidanceFile(file);
  }

  const onFileRejected = () => {
    props.alertError(siteConstants.ERROR_MSG_INVALID_GUIDANCE_DATABASE_FILE);
  }

  const handleConfirmationDialogClose = () => {
    isConfirmationDialogOpen.setFalse();
  }

  const handleContinueButtonClick = () => {
    isConfirmationDialogOpen.setFalse();
    props.updateGuidance(guidanceFile);
  }

  useEffect(() => {
    if (guidanceFile)
      isConfirmationDialogOpen.setTrue();
  }, [guidanceFile]);

  useEffect(() => {
    const _guidance = [];
    if (props.guidance) {
      props.guidance.map((row) => {
        _guidance.push({
          id: row.id,
          skill: row.skill,
          theme: row.theme,
          csText: row.cs_text,
          guidance: row.guidance,
        })
      });
    }
    setGuidance(_guidance);
  }, [props.guidance]);

  useEffect(() => {
    props.alertReset();
    props.listGuidance();
  }, []);

  return (
    <>
      <Helmet>
        <title>Guidance Database</title>
      </Helmet>

      <Container
        maxWidth="xl"
        sx={{
          marginTop: "30px",
          textAlign: "left",
        }}
      >
        <Typography variant="h4">Guidance Database</Typography>
        {
          props.alert.message &&
          <Alert variant="outlined" severity={ props.alert.type }>
            { props.alert.message }
          </Alert>
        }
        <StyledDropzone
          selectedFilePath={props.selectedFilePath}
          selectedFileSize={props.selectedFileSize}
          onFileAccepted={onFileAccepted}
          onFileRejected={onFileRejected}
        />
        <DataGrid
          rows={guidance}
          columns={columns}
          autoHeight={true}
          density="compact"
          hideFooterSelectedRowCount={true}
          loading={props.isListingGuidance || props.isUpdatingGuidance}
          sx={{ marginTop: "20px" }}
        />
      </Container>
      {guidanceFile &&
        <Dialog
          open={isConfirmationDialogOpen.value}
          onClose={handleConfirmationDialogClose}
          aria-labelledby="confirmation-dialog-title"
          aria-describedby="confirmation-dialog-description"
        >
          <DialogTitle id="confirmation-dialog-title">
            Update Guidance Database
          </DialogTitle>
          <DialogContent>
            <DialogContentText id="confirmation-dialog-description">
              You are about to update the guidance database with "{guidanceFile.name}".
              Existing database will be overwritten.
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button
              variant="contained"
              onClick={handleContinueButtonClick}
              sx={{ mt: 1, mr: 1 }}
            >
              Continue
            </Button>
            <Button
              variant="outlined"
              onClick={handleConfirmationDialogClose}
              sx={{ mt: 1, mr: 1 }}
            >
              Back
            </Button>
          </DialogActions>
        </Dialog>
      }
    </>
  );
}

const mapState = (state) => {
  const {
    isListingGuidance,
    guidance,
    isUpdatingGuidance,
  } = state.cs;
  return {
    isListingGuidance,
    guidance,
    isUpdatingGuidance,
    alert: state.alert,
  }
};

const actionCreators = {
  alertReset: alertActions.reset,
  alertError: alertActions.error,
  listGuidance: csActions.listGuidance,
  updateGuidance: csActions.updateGuidance,
};

export default connect(mapState, actionCreators)(GuidanceDatabasePage);